import { useState, useCallback } from 'react'
import { UseFormReturn } from 'react-hook-form'
import {
  useProductRecogniseWineMutation,
  useProductRecognitionResultSaveCommentMutation,
} from 'api/hooks'
import {
  buildImagesArray,
  validateRequiredFields,
  withLoadingAndError,
} from 'components/Pages/AccountSetup/Content/_hooks/useAccountSetup.helpers'
import { getLocaleId } from 'utils/getLocaleId'
import useNotification from 'utils/notification'
import { useTranslation } from 'react-i18next'
import { FIELDS, STEPS } from './index'
import { RecognitionResultProduct } from 'api/fragments/types'

interface UseRecognizeWineParams {
  methods: UseFormReturn<any>
  setStep: (step: number) => void
  onConfirm: (productRecognition: RecognitionResultProduct) => void
}

export const useRecognizeWine = ({
  methods,
  setStep,
  onConfirm,
}: UseRecognizeWineParams) => {
  const notify = useNotification()
  const { t } = useTranslation()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [recognitionSearchId, setRecognitionSearchId] = useState<string | null>(
    null
  )

  const [productRecognise] = useProductRecogniseWineMutation()

  const [productRecognitionResultSaveComment] =
    useProductRecognitionResultSaveCommentMutation()
  const handleRecognise = useCallback(async () => {
    const { bottleImages = {}, additionalImages = {} } = methods.getValues()
    setRecognitionSearchId(null)
    if (
      !validateRequiredFields(
        bottleImages,
        methods.setError,
        t('error.fieldRequired')
      )
    ) {
      return
    }

    const images = buildImagesArray(bottleImages, additionalImages)

    await withLoadingAndError(
      async () => {
        const { data: recognitionData } = await productRecognise({
          variables: {
            localeId: getLocaleId(),
            input: {
              images,
              save: false,
            },
          },
        })
        if (recognitionData?.productRecognise?.rid) {
          setRecognitionSearchId(recognitionData?.productRecognise?.rid)
        }
        const productRecognized = recognitionData?.productRecognise
        methods.setValue(FIELDS.RECOGNITION_DATA, productRecognized)
        setStep(STEPS.recognizedWine)
      },
      setIsSubmitting,
      notify
    )
  }, [methods, productRecognise, t, notify, setStep])

  const handleConfirm = useCallback(async () => {
    const { recognitionData } = methods.getValues()
    onConfirm(recognitionData)
  }, [recognitionSearchId, methods])

  const handleIncorrectRecognition = useCallback(
    async (comment: string) => {
      if (comment.length <= 1) {
        return
      }

      if (!recognitionSearchId) {
        return
      }

      const result = await withLoadingAndError(
        async () => {
          await productRecognitionResultSaveComment({
            variables: {
              input: { rid: recognitionSearchId, comment },
            },
          })
          return true
        },
        setIsSubmitting,
        notify
      )

      if (result) {
        notify(t('accountSetup.incorrectDialog.successMessage'), 'success')
      }
    },
    [productRecognitionResultSaveComment, recognitionSearchId, notify, t]
  )

  return {
    isSubmitting,
    recognitionSearchId,
    handleRecognise,
    handleConfirm,
    handleIncorrectRecognition,
  }
}
